import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import fuzzysort from "fuzzysort";
import { useCallback, useMemo } from "react";
import { api } from "../api/client";
import type {
  StaticSystem,
  StaticSystemsResponse,
  WanderingByClass,
  WhClass,
  WhDestinationByCode,
} from "../types/chain";

export interface SystemSearchResult {
  id: string;
  name: string;
  class: WhClass;
  region?: string;
}

export interface UseStaticSystemsByIdResult {
  isLoading: boolean;
  byId: (id: string) => StaticSystem | undefined;
}

export interface UseStaticSystemsSearchResult {
  isLoading: boolean;
  search: (query: string, limit?: number) => SystemSearchResult[];
}

export interface UseWanderingByClassResult {
  isLoading: boolean;
  forClass: (cls: WhClass) => string[];
}

export interface UseWhDestinationResult {
  isLoading: boolean;
  forCode: (code: string) => WhClass | null;
}

function useStaticSystemsQuery(): UseQueryResult<StaticSystemsResponse> {
  return useQuery<StaticSystemsResponse>({
    queryKey: ["static", "systems"],
    queryFn: () => api<StaticSystemsResponse>("/static/systems"),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useStaticSystemsById(): UseStaticSystemsByIdResult {
  const query = useStaticSystemsQuery();

  const byId = useMemo<Map<string, StaticSystem>>(() => {
    if (!query.data) return new Map();
    const map = new Map<string, StaticSystem>();
    for (const sys of Object.values(query.data.systems)) {
      map.set(sys.id, sys);
    }
    return map;
  }, [query.data]);

  const lookup = useCallback((id: string) => byId.get(id), [byId]);

  return {
    isLoading: query.isLoading,
    byId: lookup,
  };
}

type PreparedTarget = ReturnType<typeof fuzzysort.prepare>;

interface PreparedEntry {
  id: string;
  name: string;
  prepared: PreparedTarget;
}

interface IndexedSystems {
  byId: Map<string, StaticSystem>;
  prepared: PreparedEntry[];
}

export function useStaticSystemsSearch(): UseStaticSystemsSearchResult {
  const query = useStaticSystemsQuery();

  const indexed = useMemo<IndexedSystems | null>(() => {
    if (!query.data) return null;
    const byId = new Map<string, StaticSystem>();
    const prepared: PreparedEntry[] = [];
    for (const sys of Object.values(query.data.systems)) {
      byId.set(sys.id, sys);
      prepared.push({
        id: sys.id,
        name: sys.name,
        prepared: fuzzysort.prepare(sys.name),
      });
    }
    return { byId, prepared };
  }, [query.data]);

  return {
    isLoading: query.isLoading,
    search: (q, limit = 20) => {
      if (!indexed || !q.trim()) return [];
      const results = fuzzysort.go(q, indexed.prepared, {
        key: "prepared",
        limit,
      });
      const out: SystemSearchResult[] = [];
      for (const r of results) {
        const sys = indexed.byId.get(r.obj.id);
        if (!sys) continue;
        out.push({
          id: sys.id,
          name: sys.name,
          class: sys.class,
          region: sys.region,
        });
      }
      return out;
    },
  };
}

const EMPTY_WANDERING: string[] = [];

export function useWanderingByClass(): UseWanderingByClassResult {
  const query = useStaticSystemsQuery();
  const table: WanderingByClass | null = query.data?.wandering_by_class ?? null;
  const forClass = useCallback(
    (cls: WhClass) => table?.[cls] ?? EMPTY_WANDERING,
    [table],
  );
  return {
    isLoading: query.isLoading,
    forClass,
  };
}

export function useWhDestinationByCode(): UseWhDestinationResult {
  const query = useStaticSystemsQuery();
  const table: WhDestinationByCode | null =
    query.data?.wh_destination_by_code ?? null;
  const forCode = useCallback(
    (code: string): WhClass | null => table?.[code] ?? null,
    [table],
  );
  return {
    isLoading: query.isLoading,
    forCode,
  };
}
